import React from 'react';
import { useCurrency } from '../../../../hooks/useCurrency';
import { moneyFromMinor } from '../../../utils/UnitConversion.utils';
import BaseModal from '../../../utils/BaseModal';

interface Props {
  open: boolean;
  onClose: () => void;
}

const BuyOrderPriceIncrementsModal: React.FC<Props> = ({ open, onClose }) => {
  const { convertFromUSD, currency, formatAmountWithoutCurrency } = useCurrency();

  // Definition of thresholds based on backend logic (in Cents)
  const thresholds = [
    { limit: 500, step: 1 },
    { limit: 1000, step: 5 },
    { limit: 10000, step: 10 },
    { limit: 50000, step: 100 },
    { limit: 100000, step: 500 },
    { limit: Infinity, step: 1000 },
  ];

  const formatStep = (cents: number) => {
    const amountInUSD = moneyFromMinor(cents);
    const amountInUserCurrency = convertFromUSD(amountInUSD);
    return formatAmountWithoutCurrency(amountInUserCurrency);
  };

  return (
      <BaseModal
          open={open}
          onAbort={onClose}
          title="Buy Order Price Increments"
          maxWidth="xs"
      >
        <div className="space-y-5">
          <div className="text-sm text-neutral-400 leading-relaxed">
            Buy orders use tiered price increments based on the equivalent order value in
            <span className="text-white font-medium ml-1">{currency}</span>.
          </div>

          <div className="border border-neutral-800 bg-black/20 overflow-hidden">
            <table className="w-full text-left text-[11px] border-collapse">
              <thead>
              <tr className="bg-neutral-900/50 text-neutral-500 uppercase tracking-wider font-bold">
                <th className="px-4 py-3 border-b border-neutral-800">Order Value Range</th>
                <th className="px-4 py-3 border-b border-neutral-800 text-right">Min. Increment</th>
              </tr>
              </thead>
              <tbody className="text-neutral-300">
              {thresholds.map((t, i) => {
                const prevLimit = i === 0 ? 0 : thresholds[i - 1].limit;
                const rangeText = t.limit === Infinity
                    ? `Above ${formatStep(prevLimit)}`
                    : `${formatStep(prevLimit)} — ${formatStep(t.limit)}`;

                return (
                    <tr key={i} className="hover:bg-white/[0.02] transition-colors group">
                      <td className="px-4 py-3 border-b border-neutral-800/40 text-neutral-400 group-hover:text-neutral-200">
                        {rangeText}
                      </td>
                      <td className="px-4 py-3 border-b border-neutral-800/40 text-right font-mono text-emerald-500 font-medium">
                        +{formatStep(t.step)}
                      </td>
                    </tr>
                );
              })}
              </tbody>
            </table>
          </div>

          <div className="p-3 bg-blue-500/5 border border-blue-500/10 text-[11px] text-neutral-500 leading-normal">
            <strong className="text-neutral-400 block mb-1">Auto-Adjustment:</strong>
            When you enter a price that doesn't match the required increment, it will be automatically
            rounded down to the nearest valid price for that range.
          </div>
        </div>
      </BaseModal>
  );
};

export default BuyOrderPriceIncrementsModal;